/**
 * Coquille de la console : barre d'en-tête (sélecteur d'environnement,
 * compte), tiroir latéral (menu) et zone principale.
 * Menu : `SideMenu` (sections de `config/sections`) ; en-tête : `EnvironmentSelector`, `UserMenu`.
 */
import React, { useState } from 'react';
import { AppBar, Box, CssBaseline, Drawer, IconButton, Toolbar, Tooltip, Typography } from '@mui/material';
import { DarkMode, LightMode, Menu as MenuIcon } from '@mui/icons-material';
import { MENU_LATERAL } from '../../theme/theme';
import { useModeAffichage } from '../../theme/ModeAffichage';
import SideMenu from './SideMenu';
import EnvironmentSelector from './EnvironmentSelector';
import UserMenu from './UserMenu';

const drawerWidth = 240;

/** Hauteur de la barre d'en-tête (décalage de la zone principale). */
const APP_BAR_HEIGHT = '64px';

const drawerPaperSx = {
  boxSizing: 'border-box', width: drawerWidth, border: 0,
  backgroundColor: MENU_LATERAL.fond, color: MENU_LATERAL.texte,
} as const;

/** Bascule clair / sombre de l'en-tête. */
const BoutonMode: React.FC = () => {
  const { mode, basculer } = useModeAffichage();
  const libelle = mode === 'light' ? 'Passer en mode sombre' : 'Passer en mode clair';
  return (
    <Tooltip title={libelle}>
      <IconButton color="inherit" onClick={basculer} aria-label={libelle} sx={{ mr: 1 }}>
        {mode === 'light' ? <DarkMode /> : <LightMode />}
      </IconButton>
    </Tooltip>
  );
};

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const drawer = <SideMenu onNavigate={() => setMobileOpen(false)} />;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` } }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Administration
          </Typography>
          <BoutonMode />
          <EnvironmentSelector />
          <UserMenu />
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': drawerPaperSx }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent" open
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': drawerPaperSx }}>
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, mt: APP_BAR_HEIGHT }}>
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
